import { supabase } from '../lib/supabase';
import { OrganizationService } from './organization.service';
import {
  Deal,
  DealCreateData,
  DealUpdateData,
  DealFilters,
  DealStageHistory,
  DealCommission,
  CommissionStructure,
  SalesTarget,
  DealAttachment,
  SalesPipelineStats,
  SalespersonPerformance,
  SalesMetrics,
  SalesReportFilters,
  CommissionCalculation,
  DealProfitAnalysis,
  DealStage
} from '../types/sales';
import { mapDealStatusToLeadStage, mapStageToLeadStatus } from '../utils/leadStageMapping';
import { customerService } from './customers.service';
import { XeroService } from './xero.service';
import XeroCredentialsService from './xero-credentials.service';

export interface DealsResponse {
  data: Deal[];
  total: number;
  page: number;
  pageSize: number;
}

class SalesService {
  // Deal CRUD operations
  async getDeals(filters?: DealFilters, page = 1, pageSize = 50) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      console.log('Fetching deals for organization:', organizationId);

      // Build query with relationships
      let query = supabase
        .from('deals')
        .select(`
          *,
          customer:customers!customer_id(
            id, first_name, last_name, email, phone, mobile, lead_status,
            address, address_line1, address_line2, suburb, state, postcode,
            date_of_birth, drivers_license_number, drivers_license_expiry
          ),
          vehicle:vehicles!vehicle_id(
            id, year, make, model, trim, vin, stock_number, images, status
          ),
          documents:deal_documents(
            id, document_type, document_name, document_url, generated_at
          )
        `)
        .order('created_at', { ascending: false });
      
      // Apply organization filter
      if (organizationId) {
        query = query.or(`organization_id.eq.${organizationId},organization_id.is.null`);
      }

      // Apply non-search filters
      if (filters?.stage && filters.stage !== 'all') {
        query = query.eq('stage', filters.stage);
      }

      if (filters?.salesperson_id && filters.salesperson_id !== 'all') {
        query = query.eq('sales_rep_id', filters.salesperson_id);
      }

      if (filters?.customer_id) {
        query = query.eq('customer_id', filters.customer_id);
      }

      if (filters?.vehicle_id) {
        query = query.eq('vehicle_id', filters.vehicle_id);
      }

      if (filters?.date_from) {
        query = query.gte('inquiry_date', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('inquiry_date', filters.date_to);
      }

      if (filters?.min_value) {
        query = query.gte('total_amount', filters.min_value);
      }

      if (filters?.max_value) {
        query = query.lte('total_amount', filters.max_value);
      }

      if (filters?.payment_method && filters.payment_method !== 'all') {
        query = query.eq('payment_method', filters.payment_method);
      }

      if (filters?.lead_source && filters.lead_source !== 'all') {
        query = query.eq('lead_source', filters.lead_source);
      }

      // Get total count for pagination
      let countQuery = supabase
        .from('deals')
        .select('*', { count: 'exact', head: true });

      // Only filter by organization if one exists
      if (organizationId) {
        countQuery = countQuery.or(`organization_id.eq.${organizationId},organization_id.is.null`);
      }

      const { count } = await countQuery;

      // Fetch deals
      let data: any[];
      let error: any;

      // If search is active, fetch more deals and filter client-side
      if (filters?.search && filters.search.trim()) {
        const searchTerm = filters.search.toLowerCase().trim();

        // Fetch more deals for searching (up to 500)
        const { data: allDeals, error: fetchError } = await query.limit(500);

        if (fetchError) {
          console.error('Error fetching deals for search:', fetchError);
          throw fetchError;
        }

        // Filter client-side by deal number, customer name, or vehicle info
        const filteredDeals = (allDeals || []).filter((deal: any) => {
          const dealNumber = (deal.deal_number || '').toLowerCase();
          const customerName = deal.customer
            ? `${deal.customer.first_name || ''} ${deal.customer.last_name || ''}`.toLowerCase().trim()
            : '';
          const vehicleInfo = deal.vehicle
            ? `${deal.vehicle.year || ''} ${deal.vehicle.make || ''} ${deal.vehicle.model || ''} ${deal.vehicle.stock_number || ''}`.toLowerCase().trim()
            : '';

          return (
            dealNumber.includes(searchTerm) ||
            customerName.includes(searchTerm) ||
            vehicleInfo.includes(searchTerm)
          );
        });

        // Apply pagination to filtered results
        const offset = (page - 1) * pageSize;
        data = filteredDeals.slice(offset, offset + pageSize);
        error = null;

        console.log(`🔍 Search "${filters.search}": Found ${filteredDeals.length} matches, showing page ${page}`);
      } else {
        // No search - normal pagination
        const offset = (page - 1) * pageSize;
        const result = await query.range(offset, offset + pageSize - 1);
        data = result.data;
        error = result.error;

        if (error) {
          console.error('Error fetching deals:', error);
          throw error;
        }
      }

      console.log('Deals fetched:', data?.length || 0, 'deals');
      console.log('First deal (if any):', data?.[0]);

      // Enrich deals with salesperson data
      if (data && data.length > 0) {
        const enrichedDeals = await Promise.all(data.map(async (deal) => {
          let salesperson = null;
          let sales_rep = null;

          if (deal.salesperson_id) {
            const { data: salespersonData } = await supabase
              .from('users')
              .select('id, first_name, last_name, email, role')
              .eq('id', deal.salesperson_id)
              .single();
            salesperson = salespersonData;
          }

          if (deal.sales_rep_id && !salesperson) {
            const { data: salesRepData } = await supabase
              .from('users')
              .select('id, first_name, last_name, email, role')
              .eq('id', deal.sales_rep_id)
              .single();
            sales_rep = salesRepData;
          }

          return {
            ...deal,
            salesperson: salesperson || sales_rep
          };
        }));

        return {
          data: {
            data: enrichedDeals as Deal[],
            total: count || 0,
            page,
            pageSize
          },
          error: null
        };
      }

      return {
        data: {
          data: data as Deal[],
          total: count || 0,
          page,
          pageSize
        },
        error: null
      };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async getDeal(dealId: string) {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          customer:customers!customer_id(
            id, first_name, last_name, email, phone, mobile,
            address_line1, suburb, state, postcode, lead_status
          ),
          vehicle:vehicles!vehicle_id(
            id, year, make, model, trim, vin, stock_number, images,
            cost_price, list_price, sale_price, status
          ),
          documents:deal_documents(
            id, document_type, document_name, document_url, generated_at
          )
        `)
        .eq('id', dealId)
        .single();

      if (error || !data) {
        return { data, error };
      }

      // Fetch salesperson data separately if salesperson_id or sales_rep_id exists
      let salesperson = null;
      let sales_rep = null;

      if (data.salesperson_id) {
        const { data: salespersonData } = await supabase
          .from('users')
          .select('id, first_name, last_name, email, role')
          .eq('id', data.salesperson_id)
          .single();
        salesperson = salespersonData;
      }

      if (data.sales_rep_id) {
        const { data: salesRepData } = await supabase
          .from('users')
          .select('id, first_name, last_name, email, role')
          .eq('id', data.sales_rep_id)
          .single();
        sales_rep = salesRepData;
      }

      // Add salesperson data to the result
      const enrichedData = {
        ...data,
        salesperson,
        sales_rep
      };

      return { data: enrichedData, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async createDeal(dealData: DealCreateData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      console.log('Creating deal for organization:', organizationId);

      // Determine deal type for numbering (P for proposals, S for sales)
      const dealType = (dealData.stage === 'won' || dealData.status === 'won') ? 'sale' : 'proposal';

      // Generate deal number
      const { data: dealNumber, error: dealNumberError } = await supabase
        .rpc('generate_deal_number', {
          org_id: organizationId,
          deal_type: dealType
        });

      if (dealNumberError) {
        console.error('Error generating deal number:', dealNumberError);
        // Use fallback if function doesn't exist
      }

      // Fallback: P or S prefix with timestamp and random suffix
      const prefix = dealType === 'sale' ? 'S' : 'P';
      const finalDealNumber = dealNumber || `${prefix}${Date.now()}-${Math.random().toString(36).substring(2, 5)}`;

      // Clean up fields - convert empty strings to null (especially important for UUID fields)
      const cleanedDealData = Object.fromEntries(
        Object.entries(dealData).map(([key, value]) => [
          key,
          // Convert empty strings to null to prevent UUID and date field errors
          (typeof value === 'string' && value === '') ? null : value
        ])
      );

      const dealToInsert = {
        ...cleanedDealData,
        stage: dealData.stage || 'proposal',
        status: 'proposal', // New deals start as proposal status
        stage_updated_at: new Date().toISOString(),
        organization_id: organizationId,
        deal_number: finalDealNumber,
        created_by: user.id,
        // Ensure inquiry_date is always set
        inquiry_date: dealData.inquiry_date || new Date().toISOString()
      };

      // Check if vehicle already has a WON deal (prevent selling already-sold vehicles)
      if (dealData.vehicle_id) {
        const { data: existingDeals } = await supabase
          .from('deals')
          .select('id, deal_number, stage')
          .eq('vehicle_id', dealData.vehicle_id)
          .eq('stage', 'won'); // Only block if there's a won deal

        if (existingDeals && existingDeals.length > 0) {
          const activeDeal = existingDeals[0];
          return {
            data: null,
            error: new Error(
              `Vehicle already sold (Deal ${activeDeal.deal_number}). Cannot create new deals for sold vehicles.`
            )
          };
        }
      }

      console.log('Inserting deal:', dealToInsert);

      const { data, error } = await supabase
        .from('deals')
        .insert(dealToInsert)
        .select()
        .single();

      if (error) {
        console.error('Error creating deal:', error);
        return { data, error };
      }

      console.log('Deal created successfully:', data);

      // Automatically update vehicle status and active_deal_id based on deal stage
      if (data && dealData.vehicle_id) {
        const vehicleStatus = this.getVehicleStatusFromDealStage(data.stage);
        if (vehicleStatus) {
          // Prepare vehicle update
          const vehicleUpdate: any = {
            status: vehicleStatus,
            active_deal_id: data.id  // Track active deal
          };

          // Set sold_date when deal is created as won
          if (data.status === 'won' || data.stage === 'won') {
            vehicleUpdate.sold_date = data.actual_close_date || new Date().toISOString().split('T')[0];
          }

          await supabase
            .from('vehicles')
            .update(vehicleUpdate)
            .eq('id', dealData.vehicle_id);
          console.log(`✅ Vehicle ${dealData.vehicle_id} status updated to: ${vehicleStatus}, active_deal_id set to: ${data.id}, sold_date: ${vehicleUpdate.sold_date || 'not set'}`);
        }
      }

      // Automatically update customer lead_status based on deal stage
      if (data && dealData.customer_id) {
        let customerLeadStatus: string = 'proposal';

        switch (data.stage) {
          case 'proposal':
            customerLeadStatus = 'proposal';
            break;
          case 'won':
            customerLeadStatus = 'won';
            break;
          case 'lost':
            customerLeadStatus = 'lost';
            break;
          default:
            customerLeadStatus = 'proposal';
        }

        await supabase
          .from('customers')
          .update({ lead_status: customerLeadStatus, updated_at: new Date().toISOString() })
          .eq('id', dealData.customer_id);

        console.log(`✅ Customer ${dealData.customer_id} lead_status updated to: ${customerLeadStatus}`);
      }

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async updateDeal(dealId: string, updates: DealUpdateData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the current deal to check for stage changes and finalization status
      const { data: currentDeal } = await supabase
        .from('deals')
        .select('stage, vehicle_id, status, signature_locked')
        .eq('id', dealId)
        .single();

      // Signature lock: once both parties have signed, the deal is immutable
      // Only system fields may be updated (activity logs, signatures themselves)
      if (currentDeal?.signature_locked) {
        const signatureAllowedFields = ['dealer_signature', 'dealer_signed_at', 'signature_locked'];
        const updatingBlockedFields = Object.keys(updates).some(
          key => !signatureAllowedFields.includes(key) && updates[key as keyof DealUpdateData] !== undefined
        );
        if (updatingBlockedFields) {
          throw new Error('This deal is signed and locked. It cannot be edited.');
        }
      }

      // Prevent editing finalized deals (won, lost, refunded)
      // Exception: Allow updating certain fields even when finalized
      const finalizedStatuses = ['won', 'lost', 'refunded'];
      const isFinalized = currentDeal?.status && finalizedStatuses.includes(currentDeal.status);

      if (isFinalized) {
        // Only allow updating these specific fields for finalized deals
        const allowedFields = ['notes', 'internal_notes', 'tags'];
        const updatingRestrictedFields = Object.keys(updates).some(
          key => !allowedFields.includes(key) && updates[key as keyof DealUpdateData] !== undefined
        );

        if (updatingRestrictedFields) {
          throw new Error(
            `Cannot edit finalized deals. This deal is already ${currentDeal.status}. ` +
            `Contact your manager if changes are needed.`
          );
        }
      }

      const { data, error } = await supabase
        .from('deals')
        .update({
          ...updates,
          updated_by: user.id
        })
        .eq('id', dealId)
        .select()
        .single();

      if (error) {
        return { data, error };
      }

      // Automatically update vehicle status and active_deal_id if deal stage changed
      if (data && currentDeal && data.vehicle_id) {
        const stageChanged = updates.stage && updates.stage !== currentDeal.stage;
        const statusChanged = updates.status && updates.status !== currentDeal.status;

        if (stageChanged || statusChanged) {
          const vehicleStatus = this.getVehicleStatusFromDealStage(data.stage, data.status);
          if (vehicleStatus) {
            // Check if deal is won or lost - clear active_deal_id
            const shouldClearActiveDeal = data.status === 'won' || data.status === 'lost' || data.status === 'refunded';

            // Prepare vehicle update
            const vehicleUpdate: any = {
              status: vehicleStatus,
              active_deal_id: shouldClearActiveDeal ? null : data.id
            };

            // Set sold_date when deal is won
            if (data.status === 'won' || data.stage === 'won') {
              vehicleUpdate.sold_date = data.actual_close_date || new Date().toISOString().split('T')[0];
            }

            await supabase
              .from('vehicles')
              .update(vehicleUpdate)
              .eq('id', data.vehicle_id);
            console.log(`Vehicle ${data.vehicle_id} status updated to: ${vehicleStatus}, active_deal_id: ${shouldClearActiveDeal ? 'cleared' : data.id}, sold_date: ${vehicleUpdate.sold_date || 'not set'} (deal stage: ${data.stage})`);
          }
        }
      }

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async deleteDeal(dealId: string) {
    try {
      const { error } = await supabase
        .from('deals')
        .delete()
        .eq('id', dealId);

      return { error };
    } catch (error: any) {
      return { error };
    }
  }

  async updateDealStatus(dealId: string, stage: DealStage, notes?: string, lostReason?: string) {
    try {
      const updates: any = { stage };

      // Set status and close date for won/lost deals
      if (stage === 'won') {
        updates.status = 'won'; // Won deals
        updates.actual_close_date = new Date().toISOString().split('T')[0];
      } else if (stage === 'lost') {
        updates.status = 'lost'; // Lost deals
        updates.actual_close_date = new Date().toISOString().split('T')[0];

        // Require lost_reason when marking as lost
        if (lostReason) {
          updates.lost_reason = lostReason;
        } else {
          throw new Error('Lost reason is required when marking a deal as lost');
        }
      } else if (stage === 'refunded') {
        updates.status = 'refunded'; // Refunded deals
        updates.actual_close_date = new Date().toISOString().split('T')[0];
      } else {
        // For active deals, set status to proposal
        updates.status = 'proposal';
      }

      const result = await this.updateDeal(dealId, updates);

      // Update associated customer lead_status when deal stage changes
      if (result.data && !result.error) {
        const { data: dealData } = await supabase
          .from('deals')
          .select('customer_id')
          .eq('id', dealId)
          .single();

        if (dealData?.customer_id) {
          // Direct mapping: deal.stage → customer.lead_status
          let customerLeadStatus: string = 'contacted';

          switch (stage) {
            case 'proposal':
              customerLeadStatus = 'proposal';
              break;
            case 'won':
              customerLeadStatus = 'won';
              break;
            case 'lost':
              customerLeadStatus = 'lost';
              break;
            default:
              customerLeadStatus = 'contacted';
          }

          // Update customer lead_status
          await supabase
            .from('customers')
            .update({ lead_status: customerLeadStatus, updated_at: new Date().toISOString() })
            .eq('id', dealData.customer_id);

          console.log(`✅ Customer lead_status synced: ${customerLeadStatus} (deal stage: ${stage})`);
        }
      }

      // Update vehicle status when deal is lost
      if (stage === 'lost' && result.data) {
        const { data: dealData } = await supabase
          .from('deals')
          .select('vehicle_id')
          .eq('id', dealId)
          .single();

        if (dealData?.vehicle_id) {
          await supabase
            .from('vehicles')
            .update({ status: 'available' })
            .eq('id', dealData.vehicle_id);
        }
      }

      // Add stage history note if provided
      if (notes && result.data) {
        await this.addStageHistoryNote(dealId, notes);
      }

      // For won deals, trigger invoice generation and accounting entry
      if (stage === 'won' && result.data && !result.error) {
        try {
          // 1. Generate invoice
          await this.generateInvoiceForDeal(dealId);

          // 2. Create accounting entry
          await this.createAccountingEntryForDeal(dealId);

          // 3. Auto-sync to Xero if organization has integration enabled
          try {
            // Get deal details with organization ID
            const { data: dealData } = await supabase
              .from('deals')
              .select(`
                *,
                customer:customers!deals_customer_id_fkey(*),
                vehicle:vehicles!deals_vehicle_id_fkey(*),
                salesperson:users(*)
              `)
              .eq('id', dealId)
              .single();

            if (dealData && dealData.organization_id) {
              // Check if organization has Xero credentials
              const hasXeroCredentials = await XeroCredentialsService.hasCredentials(dealData.organization_id);

              if (hasXeroCredentials) {
                console.log('Organization has Xero integration - attempting auto-sync for won deal');

                // Check if authenticated with Xero
                const isAuthenticated = await XeroService.isAuthenticated(dealData.organization_id);

                if (isAuthenticated && dealData.customer && dealData.vehicle) {
                  // Map customer to XeroContact format
                  const xeroCustomer = {
                    name: dealData.customer.full_name || `${dealData.customer.first_name} ${dealData.customer.last_name}`.trim(),
                    firstName: dealData.customer.first_name,
                    lastName: dealData.customer.last_name,
                    emailAddress: dealData.customer.email,
                    phoneNumbers: dealData.customer.mobile ? [{
                      phoneType: 'MOBILE' as const,
                      phoneNumber: dealData.customer.mobile
                    }] : dealData.customer.phone ? [{
                      phoneType: 'DEFAULT' as const,
                      phoneNumber: dealData.customer.phone
                    }] : undefined,
                    isCustomer: true,
                  };

                  // Calculate tax breakdown for Xero
                  const total = dealData.total_amount || dealData.vehicle_price || 0;
                  const gst = dealData.gst_amount || 0;
                  const stampDuty = dealData.stamp_duty || 0;
                  const subtotal = total - gst;

                  // Prepare sync data
                  const syncData = {
                    vehicle: dealData.vehicle,
                    customer: xeroCustomer,
                    sale: {
                      date: new Date().toISOString().split('T')[0],
                      salePrice: total,
                      reference: dealData.deal_number || `DEAL-${dealData.id}`,
                      staffMember: dealData.salesperson?.full_name || dealData.salesperson?.email || 'Unknown',
                    },
                    taxCalculation: {
                      subtotal: subtotal,
                      gst: gst,
                      lct: 0,
                      stampDuty: stampDuty,
                      notionalInputTaxCredit: 0,
                      total: total,
                      breakdown: {
                        gstBreakdown: { rate: 0.1, amount: gst },
                        lctBreakdown: { threshold: 0, taxableAmount: 0, rate: 0, amount: 0 },
                        stampDutyBreakdown: { bands: [], totalAmount: stampDuty }
                      }
                    },
                    tradeIn: dealData.trade_in_allowance > 0 ? {
                      value: dealData.trade_in_allowance,
                      description: dealData.trade_in_vehicle_make
                        ? `${dealData.trade_in_vehicle_year || ''} ${dealData.trade_in_vehicle_make} ${dealData.trade_in_vehicle_model || ''}`.trim()
                        : 'Trade-in vehicle',
                    } : undefined,
                  };

                  // Sync to Xero
                  const syncResult = await XeroService.syncSaleToXero(syncData, dealData.organization_id);

                  if (syncResult.success && syncResult.invoiceId) {
                    console.log('✅ Successfully synced won deal to Xero. Invoice ID:', syncResult.invoiceId);

                    // Update deal with Xero invoice ID
                    await supabase
                      .from('deals')
                      .update({
                        xero_invoice_id: syncResult.invoiceId,
                        xero_contact_id: syncResult.contactId,
                        xero_sync_status: 'synced',
                        xero_sync_timestamp: new Date().toISOString(),
                      })
                      .eq('id', dealId);

                    await this.addStageHistoryNote(
                      dealId,
                      `Deal won and automatically synced to Xero. Invoice ID: ${syncResult.invoiceId}`
                    );
                  } else {
                    console.warn('Xero sync completed with warnings:', syncResult.errors);
                    await supabase
                      .from('deals')
                      .update({
                        xero_sync_status: 'failed',
                        xero_sync_timestamp: new Date().toISOString(),
                      })
                      .eq('id', dealId);
                  }
                } else if (hasXeroCredentials && !isAuthenticated) {
                  console.log('Xero credentials exist but not authenticated - marking for manual sync');
                  await supabase
                    .from('deals')
                    .update({
                      xero_sync_status: 'pending',
                    })
                    .eq('id', dealId);
                }
              } else {
                console.log('Organization does not have Xero integration - skipping auto-sync');
              }
            }
          } catch (xeroError: any) {
            console.warn('Xero auto-sync failed (non-critical):', xeroError);
            // Mark as failed but don't fail the deal
            await supabase
              .from('deals')
              .update({
                xero_sync_status: 'failed',
                xero_sync_timestamp: new Date().toISOString(),
              })
              .eq('id', dealId)
              .then(() => {})
              .catch((err) => console.warn('Failed to update sync status:', err));
          }

          // 4. Add note about automatic processing
          await this.addStageHistoryNote(
            dealId,
            'Deal won successfully. Commission record, invoice, and accounting entry have been automatically created.'
          );
        } catch (invoiceError: any) {
          console.error('Error generating invoice or accounting entry:', invoiceError);
          // Don't fail the whole operation - just log the error
          await this.addStageHistoryNote(
            dealId,
            `Deal won successfully. Note: Automatic invoice/accounting generation encountered an error: ${invoiceError.message}`
          );
        }
      }

      return result;
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Alias for updateDealStatus for kanban board
  async updateDealStage(dealId: string, stage: DealStage, notes?: string, lostReason?: string) {
    return this.updateDealStatus(dealId, stage, notes, lostReason);
  }

  // Deal stage history
  async getDealStageHistory(dealId: string) {
    try {
      const { data, error } = await supabase
        .from('deal_stage_history')
        .select(`
          *,
          changed_by_user:auth.users!changed_by(
            id, email, raw_user_meta_data
          )
        `)
        .eq('deal_id', dealId)
        .order('changed_at', { ascending: false });

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async addStageHistoryNote(dealId: string, notes: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current deal stage
      const { data: deal } = await supabase
        .from('deals')
        .select('stage')
        .eq('id', dealId)
        .single();

      if (!deal) throw new Error('Deal not found');

      const { data, error } = await supabase
        .from('deal_stage_history')
        .insert({
          deal_id: dealId,
          from_stage: deal.stage,
          to_stage: deal.stage,
          changed_by: user.id,
          notes
        })
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Commission management
  async getDealCommissions(dealId: string) {
    try {
      const { data, error } = await supabase
        .from('deal_commissions')
        .select(`
          *,
          salesperson:auth.users!sales_rep_id(
            id, email, raw_user_meta_data
          )
        `)
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Check commission status for a deal (uses new commission_history table)
  async getCommissionStatus(dealId: string) {
    try {
      const { data, error } = await supabase
        .from('commission_history')
        .select(`
          id,
          amount,
          status,
          commission_date,
          description,
          type,
          user:users!user_id(
            id, first_name, last_name, email
          )
        `)
        .eq('deal_id', dealId)
        .eq('type', 'sale')
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error: any) {
      return { data: [], error };
    }
  }

  async calculateCommission(
    dealId: string, 
    salespersonId: string, 
    commissionType: string = 'sale'
  ): Promise<{ data: CommissionCalculation | null; error: any }> {
    try {
      // Get deal details
      const { data: deal } = await supabase
        .from('deals')
        .select('total_amount, vehicle_price, trade_in_value')
        .eq('id', dealId)
        .single();

      if (!deal) throw new Error('Deal not found');

      // Get commission structure for salesperson
      const { data: structure } = await supabase
        .from('commission_structures')
        .select('*')
        .eq('sales_rep_id', salespersonId)
        .eq('active', true)
        .lte('effective_from', new Date().toISOString())
        .or('effective_to.is.null,effective_to.gte.' + new Date().toISOString())
        .single();

      if (!structure) {
        // Use default 2.5% if no structure found
        const baseAmount = deal.total_amount;
        const commissionRate = 2.5;
        const commissionAmount = (baseAmount * commissionRate) / 100;

        return {
          data: {
            base_amount: baseAmount,
            commission_rate: commissionRate,
            commission_amount: commissionAmount,
            commission_type: commissionType
          },
          error: null
        };
      }

      const baseAmount = deal.total_amount;
      let commissionRate = 0;
      let commissionAmount = 0;

      switch (structure.commission_type) {
        case 'percentage':
          commissionRate = structure.commission_rate || 0;
          commissionAmount = (baseAmount * commissionRate) / 100;
          break;
          
        case 'flat_rate':
          commissionAmount = structure.flat_rate_amount || 0;
          commissionRate = baseAmount > 0 ? (commissionAmount / baseAmount) * 100 : 0;
          break;
          
        case 'tiered':
          if (structure.tier_rules) {
            const tiers = structure.tier_rules as any[];
            const tier = tiers.find(t => 
              baseAmount >= t.min && (t.max === undefined || baseAmount <= t.max)
            );
            if (tier) {
              commissionRate = tier.rate;
              commissionAmount = (baseAmount * commissionRate) / 100;
            }
          }
          break;
      }

      // Apply min/max limits
      if (structure.min_commission && commissionAmount < structure.min_commission) {
        commissionAmount = structure.min_commission;
      }
      if (structure.max_commission && commissionAmount > structure.max_commission) {
        commissionAmount = structure.max_commission;
      }

      return {
        data: {
          base_amount: baseAmount,
          commission_rate: commissionRate,
          commission_amount: commissionAmount,
          commission_type: commissionType
        },
        error: null
      };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async createDealCommission(dealId: string, salespersonId: string, commissionType: string = 'sale') {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Calculate commission
      const { data: calculation, error: calcError } = await this.calculateCommission(
        dealId, 
        salespersonId, 
        commissionType
      );

      if (calcError || !calculation) {
        throw calcError || new Error('Could not calculate commission');
      }

      const { data, error } = await supabase
        .from('deal_commissions')
        .insert({
          deal_id: dealId,
          sales_rep_id: salespersonId,
          commission_type: commissionType,
          base_amount: calculation.base_amount,
          commission_rate: calculation.commission_rate,
          commission_amount: calculation.commission_amount,
          created_by: user.id
        })
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Analytics and reporting
  async getSalesPipelineStats(dateFrom?: string, dateTo?: string) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const { data, error } = await supabase
        .rpc('get_sales_pipeline_stats', {
          org_id: organizationId,
          date_from: dateFrom || null,
          date_to: dateTo || null
        });

      return { data: data as SalesPipelineStats[], error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async getSalespersonPerformance(salespersonId?: string, dateFrom?: string, dateTo?: string) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const { data, error } = await supabase
        .rpc('get_salesperson_performance', {
          org_id: organizationId,
          filter_salesperson_id: salespersonId || null,
          date_from: dateFrom || null,
          date_to: dateTo || null
        });

      return { data: data as SalespersonPerformance[], error };
    } catch (error: any) {
      return { data: null, error };
    }
  }


  async getDealProfitAnalysis(dealId: string) {
    try {
      const { data: deal, error } = await supabase
        .from('deals')
        .select(`
          *,
          vehicle:vehicles!deals_vehicle_id_fkey(cost_price, list_price)
        `)
        .eq('id', dealId)
        .single();

      if (error || !deal) throw error || new Error('Deal not found');

      // Get commissions for this deal
      const { data: commissions } = await supabase
        .from('deal_commissions')
        .select('commission_amount')
        .eq('deal_id', dealId);

      const revenue = deal.total_amount || 0;
      const cost = deal.vehicle?.cost_price || 0;
      const grossProfit = revenue - cost;
      const grossMarginPercent = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
      
      const totalCommissions = commissions?.reduce((sum, c) => sum + (c.commission_amount || 0), 0) || 0;
      const netProfit = grossProfit - totalCommissions;
      const netMarginPercent = revenue > 0 ? (netProfit / revenue) * 100 : 0;

      const analysis: DealProfitAnalysis = {
        revenue,
        cost,
        gross_profit: grossProfit,
        gross_margin_percent: grossMarginPercent,
        total_commissions: totalCommissions,
        net_profit: netProfit,
        net_margin_percent: netMarginPercent
      };

      return { data: analysis, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Analytics methods
  async getSalesMetrics(filters?: SalesReportFilters) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const { data, error } = await supabase
        .rpc('get_sales_metrics', {
          org_id: organizationId,
          date_from: filters?.date_from || null,
          date_to: filters?.date_to || null,
          filter_salesperson_id: filters?.salesperson_id !== 'all' ? filters?.salesperson_id || null : null
        });

      return { data: data?.[0] || null, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async getRevenueAnalytics(filters?: SalesReportFilters & { group_by?: string }) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const { data, error } = await supabase
        .rpc('get_revenue_analytics', {
          org_id: organizationId,
          date_from: filters?.date_from || null,
          date_to: filters?.date_to || null,
          group_by_period: filters?.group_by || 'week'
        });

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Deal attachments
  async getDealAttachments(dealId: string) {
    try {
      const { data, error } = await supabase
        .from('deal_attachments')
        .select('*')
        .eq('deal_id', dealId)
        .order('uploaded_at', { ascending: false });

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async uploadDealAttachment(
    dealId: string, 
    file: File, 
    documentType?: string, 
    description?: string
  ) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload file to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${dealId}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('deal-attachments')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('deal-attachments')
        .getPublicUrl(fileName);

      // Save attachment record
      const { data, error } = await supabase
        .from('deal_attachments')
        .insert({
          deal_id: dealId,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_url: publicUrl,
          document_type: documentType,
          description,
          uploaded_by: user.id
        })
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async deleteDealAttachment(attachmentId: string) {
    try {
      const { error } = await supabase
        .from('deal_attachments')
        .delete()
        .eq('id', attachmentId);

      return { error };
    } catch (error: any) {
      return { error };
    }
  }

  // Helper method: Map deal stage/status to vehicle status
  private getVehicleStatusFromDealStage(stage: string, status?: string): string | null {
    // Deal-driven vehicle status mapping - SIMPLIFIED
    switch (stage) {
      case 'proposal':
      case 'inquiry':
      case 'qualification':
        return 'on_offer';

      // Old stages mapped to on_offer for backward compatibility
      case 'in_progress':
      case 'negotiation':
      case 'documentation':
      case 'finance':
        return 'on_offer';

      case 'completed':
      case 'won':
        return 'sold';

      case 'lost':
        return 'available';

      default:
        return null;
    }
  }

  // Quick actions
  async convertCustomerToDeal(customerId: string, vehicleId: string, vehiclePrice: number) {
    try {
      const dealData: DealCreateData = {
        customer_id: customerId,
        vehicle_id: vehicleId,
        vehicle_price: vehiclePrice,
        total_amount: vehiclePrice,
        finance_method: 'cash',
        stage: 'proposal'
      };

      return await this.createDeal(dealData);
    } catch (error: any) {
      return { data: null, error };
    }
  }

  // Invoice generation for won deals
  async generateInvoiceForDeal(dealId: string) {
    try {
      const { data: deal } = await this.getDeal(dealId);
      if (!deal) throw new Error('Deal not found');

      // Check if invoice already exists
      const { data: existingInvoice } = await supabase
        .from('deal_documents')
        .select('id')
        .eq('deal_id', dealId)
        .eq('document_type', 'invoice')
        .single();

      if (existingInvoice) {
        console.log('Invoice already exists for deal:', dealId);
        return { data: existingInvoice, error: null };
      }

      // Generate invoice number
      const invoiceNumber = await this.generateDealInvoiceNumber();

      // Create invoice record in deal_documents
      const { data: invoice, error } = await supabase
        .from('deal_documents')
        .insert({
          deal_id: dealId,
          document_type: 'invoice',
          document_name: `Invoice ${invoiceNumber}`,
          document_number: invoiceNumber,
          generated_at: new Date().toISOString(),
          status: 'generated'
        })
        .select()
        .single();

      if (error) throw error;

      console.log(`Invoice ${invoiceNumber} generated for deal ${dealId}`);
      return { data: invoice, error: null };
    } catch (error: any) {
      console.error('Error generating invoice:', error);
      return { data: null, error };
    }
  }

  // Generate invoice number for deals
  private async generateDealInvoiceNumber(): Promise<string> {
    try {
      const now = new Date();
      const yearMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;

      // Find the last invoice number for this month
      const { data, error } = await supabase
        .from('deal_documents')
        .select('document_number')
        .eq('document_type', 'invoice')
        .like('document_number', `INV-${yearMonth}%`)
        .order('document_number', { ascending: false })
        .limit(1);

      if (error) throw error;

      let sequenceNumber = 1;

      if (data && data.length > 0 && data[0].document_number) {
        const lastNumber = data[0].document_number;
        const match = lastNumber.match(/INV-\d{6}(\d{3})/);
        if (match && match[1]) {
          sequenceNumber = parseInt(match[1]) + 1;
        }
      }

      return `INV-${yearMonth}${String(sequenceNumber).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating invoice number:', error);
      return `INV-${Date.now()}`;
    }
  }

  // Create accounting entry for won deals
  async createAccountingEntryForDeal(dealId: string) {
    try {
      const { data: deal } = await this.getDeal(dealId);
      if (!deal) throw new Error('Deal not found');

      // Check if accounting entry already exists
      const { data: existingEntry } = await supabase
        .from('accounting_entries')
        .select('id')
        .eq('deal_id', dealId)
        .eq('entry_type', 'sale')
        .single();

      if (existingEntry) {
        console.log('Accounting entry already exists for deal:', dealId);
        return { data: existingEntry, error: null };
      }

      // Create accounting entry
      const { data: entry, error } = await supabase
        .from('accounting_entries')
        .insert({
          deal_id: dealId,
          entry_type: 'sale',
          amount: deal.total_amount || 0,
          description: `Sale of ${deal.vehicle?.year} ${deal.vehicle?.make} ${deal.vehicle?.model} to ${deal.customer?.first_name} ${deal.customer?.last_name}`,
          entry_date: new Date().toISOString(),
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        // If the table doesn't exist yet, just log and continue
        if (error.code === '42P01') {
          console.log('Accounting entries table not yet created - skipping accounting entry');
          return { data: null, error: null };
        }
        throw error;
      }

      console.log(`Accounting entry created for deal ${dealId}`);
      return { data: entry, error: null };
    } catch (error: any) {
      console.error('Error creating accounting entry:', error);
      return { data: null, error };
    }
  }

  /**
   * Process refund for a deal
   * This will:
   * 1. Update deal with refund information and mark as refunded
   * 2. Return vehicle to available status
   * 3. Void Xero invoice if it exists
   * 4. Add history note
   */
  async processRefund(refundData: { deal_id: string; refund_type: 'deposit_refund' | 'full_refund'; refund_amount: number; refund_reason: string }) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the current deal
      const { data: deal, error: dealError } = await this.getDeal(refundData.deal_id);
      if (dealError || !deal) throw new Error('Deal not found');

      // 1. Update deal with refund information
      const updateData: any = {
        status: 'refunded',
        stage: 'refunded',
        refund_amount: refundData.refund_amount,
        refund_date: new Date().toISOString(),
        refund_reason: refundData.refund_reason,
        refund_type: refundData.refund_type,
        updated_at: new Date().toISOString()
      };

      // If deal has Xero invoice, mark it for voiding
      if (deal.xero_invoice_id) {
        updateData.xero_sync_status = 'voided';
      }

      const { data: updatedDeal, error: updateError } = await supabase
        .from('deals')
        .update(updateData)
        .eq('id', refundData.deal_id)
        .select()
        .single();

      if (updateError) throw updateError;

      // 2. Void Xero invoice if it exists
      if (deal.xero_invoice_id && deal.organization_id) {
        try {
          console.log(`🔄 Voiding Xero invoice ${deal.xero_invoice_id}...`);
          const voidResult = await XeroService.voidInvoice(
            deal.xero_invoice_id,
            deal.organization_id,
            `Refund: ${refundData.refund_reason}`
          );

          if (voidResult.success) {
            console.log(`✅ Xero invoice ${deal.xero_invoice_id} voided successfully`);
            await this.addStageHistoryNote(
              refundData.deal_id,
              `Xero invoice ${deal.xero_invoice_id} voided successfully`
            );
          } else {
            console.error('❌ Failed to void Xero invoice:', voidResult.errors);
            await this.addStageHistoryNote(
              refundData.deal_id,
              `Warning: Failed to void Xero invoice. Please void manually in Xero. Error: ${voidResult.errors?.join(', ')}`
            );
          }
        } catch (xeroError: any) {
          console.error('❌ Xero void error:', xeroError);
          // Don't fail the entire refund if Xero voiding fails
          await this.addStageHistoryNote(
            refundData.deal_id,
            `Warning: Failed to void Xero invoice. Please void manually in Xero. Error: ${xeroError.message}`
          );
        }
      }

      // 3. Update vehicle status back to available
      if (deal.vehicle_id) {
        await supabase
          .from('vehicles')
          .update({
            status: 'available',
            active_deal_id: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', deal.vehicle_id);

        console.log(`✅ Vehicle ${deal.vehicle_id} returned to available status`);
      }

      // 4. Add stage history note
      await this.addStageHistoryNote(
        refundData.deal_id,
        `Refund processed: ${refundData.refund_type === 'deposit_refund' ? 'Deposit' : 'Full'} refund of $${refundData.refund_amount.toFixed(2)}. Reason: ${refundData.refund_reason}`
      );

      console.log(`✅ Refund processed for deal ${refundData.deal_id}`);
      return { data: updatedDeal, error: null };
    } catch (error: any) {
      console.error('Error processing refund:', error);
      return { data: null, error };
    }
  }
}

export const salesService = new SalesService();